import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, FormField, Input } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/services/client';

/**
 * CMS sign-in.
 *
 * The error message is deliberately identical for "unknown email" and "wrong password" so the form
 * cannot be used to enumerate valid accounts. Repeated failures are locked out server-side by
 * django-axes; the message explains that rather than leaving the user confused.
 */
export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await signIn(email, password);
      navigate(redirectTo, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.isRateLimited || caught.status === 403) {
          setError('Too many failed attempts. This account is temporarily locked — try again in 15 minutes.');
        } else if (caught.isValidation) {
          setError(caught.message);
        } else {
          setError('That email and password combination was not recognised.');
        }
      } else {
        setError('We could not reach the server. Check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 no-underline hover:no-underline">
            <span className="flex h-8 w-8 items-center justify-center rounded bg-primary text-sm font-bold text-primary-contrast">
              S
            </span>
            <span className="font-display text-base font-bold text-ink">Shyena CMS</span>
          </Link>

          <h1 className="mt-10 font-display text-2xl font-bold text-ink">Sign in</h1>
          <p className="mt-2 text-sm text-muted">Manage website content, leads and applications.</p>

          <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5">
            {error && (
              <Alert tone="danger" title="Sign-in failed">
                {error}
              </Alert>
            )}

            <FormField label="Email" htmlFor="login-email" required>
              <Input
                id="login-email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={Boolean(error)}
              />
            </FormField>

            <FormField label="Password" htmlFor="login-password" required>
              <Input
                id="login-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                error={Boolean(error)}
              />
            </FormField>

            <Button type="submit" fullWidth size="lg" loading={submitting}>
              Sign in
            </Button>
          </form>

          <p className="mt-8 text-xs leading-relaxed text-muted">
            Forgotten your password? A Super Admin can issue a temporary one from Users &amp; roles. First-time setup is
            covered in the deployment guide.
          </p>
        </div>
      </div>

      <div className="band-mesh hidden w-1/2 items-center justify-center p-12 lg:flex">
        <div className="max-w-md">
          <h2 className="font-display text-3xl font-bold leading-tight text-white">
            Everything on the website, editable without a developer.
          </h2>
          <ul className="mt-8 space-y-4 text-sm text-white/75">
            {[
              'Draft, review and publish with full version history',
              'Role-based access across six roles',
              'Leads and applications tracked, not lost',
              'SEO metadata and sitemap generated automatically',
            ].map((item) => (
              <li key={item} className="flex gap-3">
                <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
